// File: src/MathTools/PolarGraphTool.js
//----------------------------------------------------------
import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

export default function PolarGraphTool({
  equations = [
    { rEqn: '2 + cos(theta)', color: 'steelblue' },
    { rEqn: '4 * sin(theta)', color: 'crimson' }
  ],
  thetaMin = 0,
  thetaMax = 2 * Math.PI,
  steps = 400,
  width = 300,
  height = 250,
  style = {}
}) {
  const svgRef = useRef(null);

  /* UI 參數 ------------------------------------------------*/
  const MARGIN_TOP   = 8;   // 圖例與邊框距離
  const CURVE_SHIFT  = 20;  // 曲線額外下移，避開圖例

  /* 1. 將 r(θ) 文字 → 可執行函式 ----------------------------*/
  function buildFn(expr) {
    const safe = expr.replace(/\^/g, '**');
    // eslint-disable-next-line no-new-func
    return new Function(
      'theta',
      `const {sin,cos,tan,abs,pow,sqrt,PI,E} = Math;
       return (${safe});`
    );
  }

  /* 2. D3 作圖 ------------------------------------------------*/
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    /* --- group 分層 --- */
    const legend = svg.append('g')
                      .attr('transform', `translate(0,${MARGIN_TOP})`);

    const plot   = svg.append('g')
                      .attr('transform', `translate(0,${MARGIN_TOP + CURVE_SHIFT})`);

    /* --- 預計算所有點 --- */
    const eqObjs = equations.map(eq => ({
      fn: buildFn(eq.rEqn),
      color: eq.color,
      label: eq.rEqn,
      pts: []
    }));

    const dt = (thetaMax - thetaMin) / steps;
    const all = [];

    eqObjs.forEach(e => {
      for (let i = 0; i <= steps; i++) {
        const θ = thetaMin + i * dt;
        const r = e.fn(θ);
        if (!Number.isFinite(r)) continue;
        const p = { x: r * Math.cos(θ), y: r * Math.sin(θ) };
        e.pts.push(p); all.push(p);
      }
    });

    if (all.length < 2) {
      legend.append('text').attr('x', 10).attr('y', 12)
            .text('No valid data.');
      return;
    }

    /* --- 比例尺 --- */
    const maxAbs = d3.max(all.flatMap(p => [Math.abs(p.x), Math.abs(p.y)])) || 1;
    const pad    = 0.1 * maxAbs;
    const range  = maxAbs + pad;

    const x = d3.scaleLinear().domain([-range, range]).range([0, width]);
    const y = d3.scaleLinear().domain([-range, range]).range([height, 0]);

    const line = d3.line().x(d => x(d.x)).y(d => y(d.y)).curve(d3.curveLinear);

    /* --- 畫曲線 --- */
    eqObjs.forEach(e =>
      plot.append('path')
          .datum(e.pts)
          .attr('fill', 'none')
          .attr('stroke', e.color)
          .attr('stroke-width', 2)
          .attr('d', line)
    );

    /* --- 畫圖例 --- */
    eqObjs.forEach((e, i) =>
      legend.append('text')
            .attr('x', 10)
            .attr('y', 12 + i * 15)
            .style('fontSize', '12px')
            .style('fill', e.color)
            .text(`r = ${e.label}`)
    );
  }, [equations, thetaMin, thetaMax, steps, width, height]);

  /* 3. 回傳 SVG ---------------------------------------------*/
  return (
    <svg
      ref={svgRef}
      width={width}
      /* 加上圖例與曲線位移的總高度 */
      height={height + MARGIN_TOP + CURVE_SHIFT}
      style={{ border: '1px solid #ccc', background: '#fff', ...style }}
    />
  );
}
